import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { AdministrationResponse, MedicationResponse } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { atTimeOfDay, timeOfDay, todayIso } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { Input, Textarea } from './Field';
import { Badge, Button, Notice, Row, Stack } from './primitives';
import { ErrorState } from './states';

/** A dose as the authorisation writes it, with its form's unit in the reader's language. */
export function useDoseText(): (medication: MedicationResponse, dose?: string) => string {
  const { t, locale } = useI18n();
  return (medication, dose = medication.dose) => {
    const unit = (locale === 'en' ? medication.unitEn : medication.unitAr) ?? medication.unitAr;
    return unit ? t('medication.doseWithUnit', { dose, unit }) : dose;
  };
}

/** Every query a dose given leaves stale: the child's medication and the class's list of what is due. */
function invalidateMedication(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['medications'] }),
    queryClient.invalidateQueries({ queryKey: ['medications-due'] }),
  ]).then(() => undefined);
}

/**
 * One current authorisation, and recording a dose of it (FR-CHD-16, FR-CHD-17), for holders of
 * `medication.administer`.
 *
 * The dose starts as the one authorised and can be changed, the time is now unless changed and never later. Where
 * today's doses have reached the authorised daily maximum, a reason is asked for before the dose is sent, as the
 * API requires; the API also refuses a lapsed authorisation, in its own words. The family is told either way.
 */
export function MedicationDose({ medication }: { medication: MedicationResponse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatNumber, settings } = useI18n();
  const doseText = useDoseText();

  const [giving, setGiving] = useState(false);
  const [dose, setDose] = useState(medication.dose);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const overLimit = medication.maxDosesPerDay !== null && medication.dosesToday >= medication.maxDosesPerDay;
  const at = time ? atTimeOfDay(todayIso(settings), time, settings) : null;
  const inFuture = at !== null && new Date(at).getTime() > Date.now();

  const give = useMutation({
    mutationFn: () => api.health.administer(medication.id, {
      doseGiven: dose.trim(),
      administeredAt: at,
      note: note.trim() || null,
      outOfScheduleReason: reason.trim() || null,
    }),
    onSuccess: async () => {
      setGiving(false);
      setNote('');
      setReason('');
      setTime('');
      await invalidateMedication(queryClient);
    },
  });

  return (
    <Stack tight>
      <Row between>
        <span className="dg-stack dg-stack--tight">
          <strong>{medication.medicationName}</strong>
          <span>{doseText(medication)} · <span className="dg-card__hint">{medication.schedule}</span></span>
        </span>
        <Badge tone={overLimit ? 'warning' : 'neutral'}>
          {medication.maxDosesPerDay !== null
            ? t('medication.dosesOfMax', { count: formatNumber(medication.dosesToday), max: formatNumber(medication.maxDosesPerDay) })
            : t('medication.dosesToday', { count: formatNumber(medication.dosesToday) })}
        </Badge>
      </Row>

      {give.isSuccess && !giving && <Notice tone="success">{t('medication.given')}</Notice>}

      {giving ? (
        <Stack>
          <label className="dg-field">
            <span className="dg-field__label">{t('medication.doseGiven')}</span>
            <Input value={dose} onChange={(event) => setDose(event.target.value)} />
          </label>
          <label className="dg-field">
            <span className="dg-field__label">{t('care.time')}</span>
            <Input type="time" value={time || timeOfDay(new Date(), settings)} onChange={(event) => setTime(event.target.value)} />
          </label>
          {inFuture && <Notice tone="warning">{t('care.time.future')}</Notice>}
          {overLimit && (
            <>
              <Notice tone="warning">{t('medication.overLimit')}</Notice>
              <label className="dg-field">
                <span className="dg-field__label">{t('medication.reason')}</span>
                <Input value={reason} onChange={(event) => setReason(event.target.value)} />
              </label>
            </>
          )}
          <Textarea rows={2} aria-label={t('care.note')} placeholder={t('care.note')} value={note}
            onChange={(event) => setNote(event.target.value)} />
          {give.isError && <ErrorState error={give.error} />}
          <Row>
            <Button
              busy={give.isPending}
              busyLabel={t('action.saving')}
              disabled={!dose.trim() || inFuture || (overLimit && !reason.trim())}
              onClick={() => give.mutate()}
            >
              {t('medication.confirmGiven')}
            </Button>
            <Button variant="ghost" onClick={() => setGiving(false)}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      ) : (
        <Row>
          <Button variant="secondary" onClick={() => { give.reset(); setGiving(true); }}>{t('medication.administer')}</Button>
        </Row>
      )}
    </Stack>
  );
}

/** The doses given, the latest first: when, how much, by whom, and why where it was outside the schedule. */
export function AdministrationList({ medication, administrations }: {
  medication: MedicationResponse; administrations: AdministrationResponse[];
}) {
  const { t, formatDateTime } = useI18n();
  const doseText = useDoseText();

  if (administrations.length === 0) return <span className="dg-card__hint">{t('medication.noneGiven')}</span>;

  return (
    <ul className="dg-stack dg-stack--tight" aria-label={t('medication.givenList')}>
      {administrations.map((given) => (
        <li key={given.id}>
          <span>{formatDateTime(given.administeredAt)} · {doseText(medication, given.doseGiven)}</span>
          {given.administeredByName && (
            <span className="dg-card__hint"> · {t('medication.givenBy', { name: given.administeredByName })}</span>
          )}
          {given.outOfScheduleReason && (
            <span className="dg-card__hint"> · {t('medication.outOfSchedule', { reason: given.outOfScheduleReason })}</span>
          )}
          {given.note && <span className="dg-card__hint"> · {given.note}</span>}
        </li>
      ))}
    </ul>
  );
}
